/**
 * PalletEditor.jsx
 *
 * Modal editor for creating/editing pallets and managing products on pallets
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useAlert } from '../../providers/AlertProvider';
import QRViewModal from "./QRViewModal";
import { PalletEditorFunctions } from "../../functions/warehouse_page/pallet_editor_functions";
import { WarehousePageFunctions } from "../../functions/warehouse_page/warehouse_page_functions";
import { 
    createPallet, 
    updatePallet, 
    fetchAllProducts,
    regenerateQRCode,
    bulkCreatePallets,
    movePalletLocation,
} from '../../api/enhancedWarehouse';
import logger from "../../utils/logger";
import { ProgressTracker } from './ProgressTracker';

const PALLET_HEIGHTS = ['A','B','C','D','E','F','G','H','I','J'];


export default function PalletEditor({
    pallet = null,
    allCustomers = [],
    allPallets = [],
    color,
    onSave,
    onClose,
    onRefreshData,
    onUpdatePallet,
    isCustomer = false,
    userProfile = null,
    canEdit = true,
    readOnly = false,
}) {
    const { showAlert } = useAlert();
    const palletFunctions = new PalletEditorFunctions(showAlert);
    const warehouseFunctions = new WarehousePageFunctions(showAlert);
    const productDropdownRef = useRef(null);
    const destinationDropdownRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);

    const [form, setForm] = useState({
        pallet_code: "",
        warehouse_no: "",
        lane: "",
        location: "",
        position: "",
        pallet_type: "mixed_sku",
        notes: "",
        status: "active",
    });
    const [availableProducts, setAvailableProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState("");
    const [selectedProductValid, setSelectedProductValid] = useState(false); // Track if selection is valid
    const [productSearchTerm, setProductSearchTerm] = useState("");
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [productQuantity, setProductQuantity] = useState("");
    const [productNotes, setProductNotes] = useState("");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [addingProduct, setAddingProduct] = useState(false);
    const [palletItems, setPalletItems] = useState([]);
    const [editingItemId, setEditingItemId] = useState(null);
    const [removingItemId, setRemovingItemId] = useState(null);
    const [movingItemId, setMovingItemId] = useState(null);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [destinationPalletId, setDestinationPalletId] = useState("");
    const [destinationPalletSearch, setDestinationPalletSearch] = useState("");
    const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
    const [selectedDestinationPallet, setSelectedDestinationPallet] = useState(null);
    const [showMovePalletLocationModal, setShowMovePalletLocationModal] = useState(false);
    const [destinationPalletIdForMove, setDestinationPalletIdForMove] = useState("");
    const [destinationPalletSearchForMove, setDestinationPalletSearchForMove] = useState("");
    const [showDestinationPalletsDropdown, setShowDestinationPalletsDropdown] = useState(false);
    const [selectedDestinationPalletForMove, setSelectedDestinationPalletForMove] = useState(null);
    const [editedQuantities, setEditedQuantities] = useState({});
    const [editedNotes, setEditedNotes] = useState({});
    const [isGeneratingQR, setIsGeneratingQR] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    
    // Bulk creation states
    const [createMultiple, setCreateMultiple] = useState(false);
    const [locationStart, setLocationStart] = useState("");
    const [locationEnd, setLocationEnd] = useState("");
    const [laneStart, setLaneStart] = useState("");
    const [laneEnd, setLaneEnd] = useState("");
    const [positionStart, setPositionStart] = useState("");
    const [positionEnd, setPositionEnd] = useState("");

    // Progress tracking states for bulk creation
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [bulkStatus, setBulkStatus] = useState({ message: '', type: 'info' });

    // Determine if we're in dark mode based on color properties
    const isDark = color.bg === "#080808";

    // Function to generate pallet code from components
    const generatePalletCode = (warehouse_no, lane, location, position) => {
        if (warehouse_no && lane && location && position) {
            const formattedLocation = location.toString().padStart(3, '0');
            return `W${warehouse_no}${lane}${formattedLocation}${position}`;
        }
        return "";
    };

    // Function to parse existing pallet code into components
    const parsePalletCode = (palletCode) => {
        if (!palletCode || palletCode.length < 7) return {};
        
        // Assuming format: W + WarehouseNo(1-2 digits) + Lane(1 char) + Location(3 digits) + Position(1 char)
        // Example: "W1A001A" = Warehouse 1, Lane A, Location 001, Position A
        const match = palletCode.match(/^W(\d+)([A-Z])(\d{3})([A-J])$/);
        if (match) {
            return {
                warehouse_no: match[1],
                lane: match[2],
                location: match[3],
                position: match[4]
            };
        }
        return {};
    };

    useEffect(() => {
        if (pallet) {
            const parsedComponents = parsePalletCode(pallet.pallet_code);
            setForm({
                pallet_code: pallet.pallet_code || "",
                warehouse_no: parsedComponents.warehouse_no || "",
                lane: parsedComponents.lane || "",
                location: parsedComponents.location || "",
                position: parsedComponents.position || "",
                pallet_type: pallet.pallet_type || "mixed_sku",
                notes: pallet.notes || "",
                status: pallet.status || "active",
                qr_link: pallet.qr_link || null,
            });
            // Load existing pallet items
            setPalletItems(pallet.items || []);
        } else {
            setPalletItems([]);
        }
        if (!readOnly) fetchProducts();
    }, [pallet, readOnly]);

    // Update pallet_code whenever components change (only for single pallet creation)
    useEffect(() => {
        if (createMultiple || pallet) return; // Don't auto-generate for bulk creation or editing
        
        const newPalletCode = generatePalletCode(
            form.warehouse_no,
            form.lane,
            form.location,
            form.position
        );
        if (newPalletCode && newPalletCode !== form.pallet_code) {
            setForm(prev => ({
                ...prev,
                pallet_code: newPalletCode
            }));
        }
    }, [form.warehouse_no, form.lane, form.location, form.position, createMultiple, pallet]);

    // Clear product selection when customer changes
    useEffect(() => {
        clearProductSelection();
    }, [form.customer_id]);

    // Add a ref to track if we should update the parent (avoid updates during initialization)
    const shouldUpdateParent = useRef(false);
    
    // Set the flag to allow updates after initial load
    useEffect(() => {
        // Allow updates after a short delay to ensure component is fully initialized
        const timer = setTimeout(() => {
            shouldUpdateParent.current = true;
        }, 100);
        
        return () => clearTimeout(timer);
    }, [pallet]);

    // Notify parent when pallet items change (for real-time updates in parent component)
    useEffect(() => {
        if (pallet && onUpdatePallet && shouldUpdateParent.current && palletItems.length >= 0) { // >= 0 to include empty array
            // Only update if palletItems actually changed
            const currentItemsJson = JSON.stringify(pallet.items || []);
            const newItemsJson = JSON.stringify(palletItems);
            
            if (currentItemsJson !== newItemsJson) {
                // Use setTimeout to avoid setState during render
                setTimeout(() => {
                    const updatedPallet = {
                        ...pallet,
                        items: palletItems
                    };
                    onUpdatePallet(updatedPallet);
                }, 0);
            }
        }
    }, [palletItems, pallet, onUpdatePallet]);

    const fetchProducts = async () => {
        try {
            const response = await fetchAllProducts();
            
            if (response.status === 200 && response.data?.success) {
                setAvailableProducts(response.data.products);
            }
        } catch (error) {
            logger.error('Error fetching products:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        const newErrors = {};
        if (!form.warehouse_no) newErrors.warehouse_no = "Warehouse number is required";
        
        // Different validation for bulk vs single creation
        if (createMultiple && !pallet) {
            // Bulk creation validation - use ranges
            if (!locationStart) newErrors.locationStart = "Start location is required";
            else if (!/^\d{1,3}$/.test(locationStart)) newErrors.locationStart = "Start location must be 1-3 digits";
            
            if (!locationEnd) newErrors.locationEnd = "End location is required";
            else if (!/^\d{1,3}$/.test(locationEnd)) newErrors.locationEnd = "End location must be 1-3 digits";
            
            if (locationStart && locationEnd && parseInt(locationStart) > parseInt(locationEnd)) {
                newErrors.locationEnd = "End location must be greater than or equal to start location";
            }

            // Lane range validation
            if (!laneStart) newErrors.laneStart = "Start lane is required";
            else if (!/^[A-Z]$/.test(laneStart)) newErrors.laneStart = "Start lane must be a single letter A-Z";
            
            if (!laneEnd) newErrors.laneEnd = "End lane is required";
            else if (!/^[A-Z]$/.test(laneEnd)) newErrors.laneEnd = "End lane must be a single letter A-Z";
            
            if (laneStart && laneEnd && laneStart > laneEnd) {
                newErrors.laneEnd = "End lane must be greater than or equal to start lane";
            }

            // Position range validation
            if (!positionStart) newErrors.positionStart = "Start position is required";
            else if (!/^[A-J]$/.test(positionStart)) newErrors.positionStart = "Start position must be A-J";
            
            if (!positionEnd) newErrors.positionEnd = "End position is required";
            else if (!/^[A-J]$/.test(positionEnd)) newErrors.positionEnd = "End position must be A-J";
            
            if (positionStart && positionEnd && positionStart > positionEnd) {
                newErrors.positionEnd = "End position must be greater than or equal to start position";
            }
        } else {
            // Single creation validation - use individual fields
            if (!form.lane) newErrors.lane = "Lane is required";
            if (!form.position) newErrors.position = "Position is required";
            
            // Location validation for single pallet
            if (!form.location) newErrors.location = "Location is required";
            else if (!/^\d{3}$/.test(form.location)) newErrors.location = "Location must be exactly 3 digits (001-999)";
        }
        
        // Check pallet code uniqueness (only for single pallet creation/editing)
        if (!createMultiple && form.pallet_code) {
            const existingPallet = allPallets.find(p => 
                p.pallet_code === form.pallet_code && (!pallet || p.id !== pallet.id)
            );
            if (existingPallet) {
                newErrors.pallet_code = `Pallet code "${form.pallet_code}" already exists`;
            }
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        try {
            let response;
            
            if (pallet) {
                // Editing existing pallet
                response = await updatePallet(pallet.id, form);
            } else if (createMultiple) {
                // Bulk creation with progress tracking
                setIsBulkProcessing(true);
                setBulkProgress({ current: 0, total: 0, percentage: 0 });
                setBulkStatus({ message: 'Starting bulk pallet creation...', type: 'info' });
                
                try {
                    const bulkData = {
                        warehouse_no: form.warehouse_no,
                        lane_start: laneStart,
                        lane_end: laneEnd,
                        location_start: locationStart,
                        location_end: locationEnd,
                        position_start: positionStart,
                        position_end: positionEnd,
                        pallet_type: form.pallet_type
                    };
                    
                    const result = await bulkCreatePallets(bulkData, (progress) => {
                        // Real-time progress callback
                        setBulkProgress({
                            current: progress.processed,
                            total: progress.total,
                            percentage: progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0
                        });
                        
                        setBulkStatus({
                            message: progress.message || `Creating pallets: ${progress.processed}/${progress.total}`,
                            type: 'info'
                        });
                    });
                    
                    if (result.success) {
                        setBulkProgress({
                            current: result.processed,
                            total: result.total,
                            percentage: 100
                        });
                        
                        setBulkStatus({
                            message: `✅ Complete! Created: ${result.success_count}/${result.processed}, Errors: ${result.error_count}`,
                            type: result.error_count > 0 ? 'warning' : 'success'
                        });
                        
                        // Show detailed results
                        if (result.error_count > 0 && result.errors && result.errors.length > 0) {
                            const errorDetails = result.errors.slice(0, 3).join('\n• ');
                            showAlert({
                                message: `${result.message}\n\n📊 Results:\n• Success: ${result.success_count}\n• Errors: ${result.error_count}\n\n❌ Sample errors:\n• ${errorDetails}${result.errors.length > 3 ? `\n...and ${result.errors.length - 3} more errors` : ''}`,
                                type: 'warning',
                                buttons: [{ label: "Close", type: "primary" }],
                            });
                        } else {
                            showAlert({
                                message: `Successfully created ${result.success_count} pallets (Lanes ${laneStart}-${laneEnd}, Locations ${locationStart.padStart(3, '0')}-${locationEnd.padStart(3, '0')}, Positions ${positionStart}-${positionEnd})`,
                                type: "success",
                                buttons: [{ label: "Close", type: "primary" }],
                            });
                        }
                        
                        onSave();
                    } else {
                        setBulkStatus({
                            message: `❌ Error: ${result.error}`,
                            type: 'danger'
                        });
                        
                        showAlert({
                            message: result.error,
                            type: "danger",
                            buttons: [{ label: "Close", type: "danger" }],
                        });
                    }
                } catch (error) {
                    setBulkStatus({
                        message: `❌ Error: ${error.message}`,
                        type: 'danger'
                    });
                    
                    showAlert({
                        message: 'Failed to create pallets: ' + error.message,
                        type: "danger",
                        buttons: [{ label: "Close", type: "danger" }],
                    });
                } finally {
                    setIsBulkProcessing(false);
                    setLoading(false);
                }
                return;
            } else {
                // Single creation
                response = await createPallet(form.pallet_code, form.pallet_type);
            }

            if (response.success || response.status === 200 || response.status === 201) {
                showAlert({
                    message: response.message || (pallet ? "Pallet updated successfully" : "Pallet created successfully"),
                    type: "success",
                    buttons: [{ label: "Close", type: "primary" }],
                });
                onSave();
            } else {
                throw new Error(response.message || 'Operation failed');
            }
        } catch (error) {
            logger.error('Error saving pallet:', error);
            showAlert({
                message: `Failed to save pallet: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }],
            });
        } finally {
            setLoading(false);
        }
    };

    const generateQRCode = async () => {
        await warehouseFunctions.generateQRCode('pallet', pallet, onSave, setIsGeneratingQR);
    };

    // Handler for regenerating QR codes
    const handleRegenerateQR = async (itemId, table) => {
        try {
            const result = await regenerateQRCode(itemId, table);
            
            if (result.success) {
                showAlert({
                    message: result.message || 'QR code regenerated successfully!',
                    type: 'success'
                });
                // Trigger refresh by calling onSave
                if (onSave) {
                    await onSave();
                }
                // Close the QR modal to force re-opening with new data
                setShowQRModal(false);
            } else {
                showAlert({
                    message: result.error || result.message || 'Failed to regenerate QR code',
                    type: 'error'
                });
            }
        } catch (error) {
            logger.error('Error regenerating QR code:', error);
            showAlert({
                message: 'Error regenerating QR code',
                type: 'error'
            });
        }
    };

    const addProductToPallet = async () => {
        setAddingProduct(true);
        try {
            const result = await palletFunctions.addProductToPallet(
                pallet,
                selectedProduct,
                productQuantity,
                productNotes,
                availableProducts,
                setPalletItems,
                setSelectedProduct,
                setProductQuantity,
                setProductNotes,
                palletItems
            );
            
            // Reset the product search after successful addition
            if (result.success) {
                clearProductSelection();
            }
        } catch (error) {
            logger.error('Unexpected error in addProductToPallet:', error);
        } finally {
            setAddingProduct(false);
        }
    };

    const updateItemQuantity = async (itemId, newQuantity, notes) => {
        setRemovingItemId(itemId); // Use same loading state
        try {
            const result = await palletFunctions.updateItemQuantity(
                pallet,
                itemId,
                newQuantity,
                notes,
                setPalletItems,
                setEditedQuantities,
                setEditedNotes,
                setEditingItemId
            );
        } catch (error) {
            logger.error('Unexpected error in updateItemQuantity:', error);
        } finally {
            setRemovingItemId(null);
        }
    };

    const removeItem = async (itemId) => {

        let palletId = pallet.id;
        let productId = null;
        
        // Check if this looks like a composite key first, regardless of whether there's a match
        if (typeof itemId === 'string' && itemId.includes('_')) {
            // Check if it looks like a composite key: pallet_id_product_id format
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            const parts = itemId.split('_');
            
            if (parts.length === 2 && uuidPattern.test(parts[0]) && uuidPattern.test(parts[1])) {
                // This looks like a composite key from loadProductPalletDetails: pallet_id_product_id
                palletId = parts[0];
                productId = parts[1];
                
            } else {
                logger.log('🔍 ItemId contains underscore but not in composite UUID format:', itemId);
            }
        } else {
            productId = itemId;
        }
        
        setRemovingItemId(itemId);
        try {
            console.log('� CALLING API TO REMOVE ITEM:', {
                productId: productId,
                originalItemId: itemId,
                palletData: pallet
            });
            
            let serverItemId = productId;
            
            const palletItemResponse = await palletFunctions.getItemId(
                palletId,
                productId
            );

            if (palletItemResponse) {
                serverItemId = palletItemResponse;
            }
            
            const result = await palletFunctions.removeItem(
                pallet,
                serverItemId,
                setPalletItems,
                productId
            );
            
            // If removal was successful, also trigger a refresh if we're in all pallets view
            if (result.success && onRefreshData) {
                logger.log('🔄 Item removed successfully, triggering refresh...');
                await onRefreshData();
            }
        } catch (error) {
            logger.error('Unexpected error in removeItem:', error);
        } finally {
            setRemovingItemId(null);
        }
    };

    const moveItem = async (itemId, destinationPalletId) => {
        setMovingItemId(itemId);
        setIsLoading(true);
        try {
            // Validate destination pallet selection
            const result = await palletFunctions.moveItem(
                pallet.id,
                itemId,
                destinationPalletId,
                setPalletItems,
                onRefreshData
            );
            
            if (result.success) {
                setShowMoveModal(false);
                setDestinationPalletId("");
                clearDestinationPalletSelection();
                setMovingItemId(null);
                setIsLoading(false);
                
                // Update local state: remove item from current pallet
                const movedItem = palletItems.find(item => 
                    item.id === itemId || 
                    `${item.pallet_id}_${item.product_id}` === itemId
                );
                
                if (movedItem) {
                    // Remove item from current pallet's items
                    const updatedPalletItems = palletItems.filter(item => 
                        item.id !== itemId && 
                        `${item.pallet_id}_${item.product_id}` !== itemId
                    );
                    setPalletItems(updatedPalletItems);
                    
                    // If onUpdatePallet is provided, update the parent component's pallet data
                    if (onUpdatePallet && pallet) {
                        const newTotalQuantity = updatedPalletItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
                        const updatedPallet = {
                            ...pallet,
                            items: updatedPalletItems,
                            total_quantity: newTotalQuantity,
                            occupied: updatedPalletItems.length > 0 || newTotalQuantity > 0
                        };
                        onUpdatePallet(updatedPallet);
                    }
                    
                    // Also call onRefreshData to update parent warehouse state
                    if (onRefreshData && movedItem) {
                        await onRefreshData({
                            type: 'move',
                            sourcePalletId: pallet.id,
                            destinationPalletId: destinationPalletId,
                            movedItem: {
                                ...movedItem,
                                quantity: movedItem.quantity || 0
                            },
                            // Calculate remaining items count for source pallet
                            sourceRemainingCount: updatedPalletItems.length,
                            sourceTotalQuantity: updatedPalletItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
                        });
                    }
                }
            }
        } catch (error) {
            logger.error('Unexpected error in moveItem:', error);
        } finally {
            setMovingItemId(null);
            setIsLoading(false);
        }
    };

    const movePalletToNewLocation = async () => {
        if (!pallet || !destinationPalletIdForMove) {
            showAlert({
                message: "Please select a destination pallet",
                type: "warning",
                buttons: [{ label: "Close", type: "primary" }]
            });
            return;
        }

        setLoading(true);
        try {
            // Call the API to move the pallet location
            const result = await movePalletLocation(pallet.id, destinationPalletIdForMove);
            
            if (result.success) {
                showAlert({
                    message: result.message || `Products moved successfully`,
                    type: "success",
                    buttons: [{ label: "Close", type: "primary" }]
                });
                
                setShowMovePalletLocationModal(false);
                setDestinationPalletIdForMove("");
                setSelectedDestinationPalletForMove(null);
                setDestinationPalletSearchForMove("");
                
                // Trigger refresh to update the view
                if (onRefreshData) {
                    await onRefreshData();
                }
                
                // Close the pallet editor since products have been moved
                onSave();
            } else {
                throw new Error(result.message || 'Failed to move products');
            }
        } catch (error) {
            logger.error('Error moving pallet location:', error);
            showAlert({
                message: `Failed to move products: ${error.message}`,
                type: "danger",
                buttons: [{ label: "Close", type: "primary" }]
            });
        } finally {
            setLoading(false);
        }
    };

    // Filter available pallets for move operation (same customer, different pallet, active status)
    const availablePalletsForMove = allPallets.filter(p => 
        p.pallet_id !== pallet?.pallet_id && 
        (p.status === 'active' || p.pallet_status === 'active')
    );

    // Get available empty pallets for pallet location move (same customer, excluding current pallet)
    const availableEmptyPallets = allPallets.filter(p => 
        p.pallet_id !== pallet?.pallet_id &&
        (p.status === 'active' || p.pallet_status === 'active') &&
        (!p.occupied || p.total_quantity === 0) // Empty pallets
    );

    // Filter destination pallets based on search term
    const filteredDestinationPallets = availablePalletsForMove.filter(p => {
        if (!destinationPalletSearch.trim()) return true;
        const searchLower = destinationPalletSearch.toLowerCase();
        return (
            p.pallet_code?.toLowerCase().includes(searchLower) ||
            p.pallet_id?.toLowerCase().includes(searchLower)
        );
    });

    // Filter empty pallets based on search term for location move
    const filteredEmptyPallets = availableEmptyPallets.filter(p => {
        if (!destinationPalletSearchForMove.trim()) return true;
        const searchLower = destinationPalletSearchForMove.toLowerCase();
        return (
            p.pallet_code?.toLowerCase().includes(searchLower) ||
            p.pallet_id?.toLowerCase().includes(searchLower)
        );
    });

    const customerProducts = availableProducts.filter(p => {
        // Filter by customer first
        const matchesCustomer = !form.customer_id || p.customer_id === parseInt(form.customer_id);
        
        // Then filter by search term if there is one
        if (!productSearchTerm.trim()) {
            return matchesCustomer;
        }
        
        const searchLower = productSearchTerm.toLowerCase();
        const matchesSearch = 
            p.product_code?.toLowerCase().includes(searchLower) ||
            p.product_name?.toLowerCase().includes(searchLower) ||
            p.batch_no?.toLowerCase().includes(searchLower);
            
        return matchesCustomer && matchesSearch;
    });

    // Handle product selection from dropdown
    const handleProductSelect = (product) => {
        setSelectedProduct(product.id);
        setSelectedProductValid(true); // Mark as valid selection
        setProductSearchTerm(`${product.product_name} (${product.product_code}${product.batch_no ? ` • Batch: ${product.batch_no}` : ''})`);
        setShowProductDropdown(false);
    };

    // Handle search input changes
    const handleProductSearchChange = (e) => {
        const value = e.target.value;
        setProductSearchTerm(value);
        setShowProductDropdown(true);
        
        // Invalidate selection when user starts typing (unless just clearing)
        if (selectedProductValid && value.trim() !== "") {
            setSelectedProductValid(false);
        }
        
        // Clear selection completely if search is empty
        if (value.trim() === "") {
            setSelectedProduct("");
            setSelectedProductValid(false);
        }
    };

    // Clear product selection
    const clearProductSelection = () => {
        setSelectedProduct("");
        setSelectedProductValid(false);
        setProductSearchTerm("");
        setShowProductDropdown(false);
    };

    // Handle destination pallet search
    const handleDestinationPalletSearchChange = (e) => {
        const value = e.target.value;
        setDestinationPalletSearch(value);
        setShowDestinationDropdown(true);
        
        // Clear selection if user starts typing
        if (selectedDestinationPallet && value.trim() !== "") {
            setSelectedDestinationPallet(null);
            setDestinationPalletId("");
        }
        
        // Clear selection if search is empty
        if (value.trim() === "") {
            setSelectedDestinationPallet(null);
            setDestinationPalletId("");
        }
    };

    // Handle destination pallet search for location move
    const handleDestinationPalletSearchForMoveChange = (e) => {
        const value = e.target.value;
        setDestinationPalletSearchForMove(value);
        setShowDestinationPalletsDropdown(true);
        
        // Clear selection if user starts typing
        if (selectedDestinationPalletForMove && value.trim() !== "") {
            setSelectedDestinationPalletForMove(null);
            setDestinationPalletIdForMove("");
        }
        
        // Clear selection if search is empty
        if (value.trim() === "") {
            setSelectedDestinationPalletForMove(null);
            setDestinationPalletIdForMove("");
        }
    };

    // Select destination pallet
    const selectDestinationPallet = (pallet) => {
        setSelectedDestinationPallet(pallet);
        setDestinationPalletId(pallet.pallet_id);
        setDestinationPalletSearch(pallet.pallet_code);
        setShowDestinationDropdown(false);
    };

    // Select destination pallet for location move
    const selectDestinationPalletForMove = (pallet) => {
        setSelectedDestinationPalletForMove(pallet);
        setDestinationPalletIdForMove(pallet.pallet_id)
        setDestinationPalletSearchForMove(`${pallet.pallet_code}`);
        setShowDestinationPalletsDropdown(false);
    };

    // Clear destination pallet selection
    const clearDestinationPalletSelection = () => {
        setSelectedDestinationPallet(null);
        setDestinationPalletId("");
        setDestinationPalletSearch("");
        setShowDestinationDropdown(false);
    };

    // Clear destination pallet selection for location move
    const clearDestinationPalletSelectionForMove = () => {
        setSelectedDestinationPalletForMove(null);
        setDestinationPalletIdForMove("");
        setDestinationPalletSearchForMove("");
        setShowDestinationPalletsDropdown(false);
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
            if (destinationDropdownRef.current && !destinationDropdownRef.current.contains(event.target)) {
                setShowDestinationDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "1rem",
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                style={{
                    backgroundColor: color.panel,
                    color: color.fg,
                    border: `2px solid ${color.border}`,
                    borderRadius: 8,
                    width: "100%",
                    maxWidth: "90%",
                    maxHeight: "90vh",
                    overflow: "auto",
                }}
            >
                <div
                    style={{
                        borderBottom: `1px solid ${color.border}`,
                        padding: "1rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <h2 style={{ margin: 0 }}>
                        {pallet ? `${readOnly ? 'View' : 'Edit'} Pallet: ${pallet.pallet_code}` : "Create New Pallet"}
                    </h2>
                    
                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        {/* QR Code Display or Placeholder */}
                        {pallet && (
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center", 
                                gap: "0.25rem" 
                            }}>
                                {(form.qr_link || pallet.qr_link) ? (
                                    <img 
                                        src={form.qr_link || pallet.qr_link} 
                                        alt="Pallet QR Code"
                                        style={{ 
                                            width: "60px", 
                                            height: "60px",
                                            border: "1px solid #ccc",
                                            borderRadius: "4px",
                                            cursor: "pointer"
                                        }}
                                        onClick={() => setShowQRModal(true)}
                                    />
                                ) : readOnly ? (
                                    <div
                                        style={{
                                            width: "60px",
                                            height: "60px",
                                            border: "2px dashed #ccc",
                                            borderRadius: "4px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: color.bg,
                                            color: color.fg,
                                            fontSize: "0.7rem",
                                            textAlign: "center",
                                        }}
                                    >
                                        No QR
                                    </div>
                                ) : (
                                    <div
                                        onClick={generateQRCode}
                                        style={{
                                            width: "60px",
                                            height: "60px",
                                            border: "2px dashed #ccc",
                                            borderRadius: "4px",
                                            cursor: isGeneratingQR ? "not-allowed" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: color.bg,
                                            opacity: isGeneratingQR ? 0.7 : 1,
                                            transition: "all 0.2s ease"
                                        }}
                                        title="Click to generate QR code"
                                    >
                                        <span style={{ 
                                            fontSize: "24px", 
                                            color: "#ccc",
                                            userSelect: "none"
                                        }}>
                                            {isGeneratingQR ? "⏳" : "📱"}
                                        </span>
                                    </div>
                                )}
                                <small style={{ fontSize: "0.7rem", color: "#666", textAlign: "center" }}>
                                    {(form.qr_link || pallet.qr_link) ? "Click to enlarge" : readOnly ? "Not generated" : "Click to generate"}
                                </small>
                            </div>
                        )}
                        
                        {/* QR Generation Button */}
                        {/* {pallet && (
                            <button
                                type="button"
                                onClick={generateQRCode}
                                disabled={isGeneratingQR}
                                style={{
                                    padding: "0.25rem 0.5rem",
                                    backgroundColor: isGeneratingQR ? "#6c757d" : "#17a2b8",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "3px",
                                    cursor: isGeneratingQR ? "not-allowed" : "pointer",
                                    fontSize: "0.6rem",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {isGeneratingQR ? "Generating..." : (form.qr_link || pallet.qr_link) ? "Regenerate QR" : "Generate QR"}
                            </button>
                        )} */}
                        
                        <button
                            onClick={onClose}
                            style={{
                                background: "none",
                                border: "none",
                                fontSize: "1.5rem",
                                cursor: "pointer",
                                color: color.fg,
                            }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: "1rem" }}>
                    {/* Progress tracker for bulk creation */}
                    {isBulkProcessing && (
                        <ProgressTracker
                            title="Bulk Pallet Creation"
                            progress={bulkProgress}
                            status={bulkStatus}
                            isProcessing={isBulkProcessing}
                            showProgress={true}
                        />
                    )}

                    <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr", 
                        gap: "1rem",
                        marginBottom: "1rem"
                    }}>
                        {/* Pallet Code Components */}
                        <div style={{ gridColumn: "1 / -1" }}>
                            {pallet ? (
                                // When editing existing pallet - show pallet code as read-only field
                                <>
                                    <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", fontSize: "1.1rem" }}>
                                        Pallet Code
                                    </label>
                                    <div style={{ 
                                        padding: "0.75rem", 
                                        backgroundColor: color.headerBg, 
                                        border: `2px solid ${color.border}`,
                                        borderRadius: 4,
                                        marginBottom: "0.5rem"
                                    }}>
                                        <span style={{ 
                                            fontSize: "1.4rem", 
                                            fontWeight: "bold", 
                                            color: color.highlight,
                                            letterSpacing: "0.1em"
                                        }}>
                                            {form.pallet_code}
                                        </span>
                                        <div style={{ 
                                            fontSize: "0.9rem", 
                                            color: color.fg,
                                            marginTop: "0.25rem",
                                            opacity: 0.8
                                        }}>
                                            Location cannot be changed for existing pallets
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // When creating new pallet - show component dropdowns
                                <>
                                    <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", fontSize: "1.1rem" }}>
                                        Pallet Location Components *
                                    </label>
                                    
                                    <div style={{ 
                                        display: "grid", 
                                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                        gap: "0.75rem",
                                        marginBottom: "0.5rem"
                                    }}>
                                        {/* Warehouse Number */}
                                        <div>
                                            <label style={{ fontWeight: "bold", fontSize: "0.9rem", marginBottom: "0.25rem", display: "block", textAlign: "left" }}>
                                                Warehouse No. *
                                            </label>
                                            <select
                                                value={form.warehouse_no}
                                                onChange={(e) => setForm(prev => ({ ...prev, warehouse_no: e.target.value }))}
                                                style={{
                                                    width: "100%",
                                                    padding: "0.5rem",
                                                    border: `1px solid ${errors.warehouse_no ? '#dc3545' : color.border}`,
                                                    borderRadius: 4,
                                                    backgroundColor: color.bg,
                                                    color: color.fg,
                                                }}
                                            >
                                                <option value="">Select</option>
                                                {Array.from({length: 99}, (_, i) => i + 1).map(num => (
                                                    <option key={num} value={num}>{num}</option>
                                                ))}
                                            </select>
                                            {errors.warehouse_no && (
                                                <div style={{ color: '#dc3545', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                                                    {errors.warehouse_no}
                                                </div>
                                            )}
                                        </div>

                                        {/* Lane */}
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                                                <label style={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                                                    {createMultiple && !pallet ? "Lane Range *" : "Lane *"}
                                                </label>
                                            </div>
                                            {createMultiple && !pallet ? (
                                                // Lane Range Inputs
                                                <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                                    <select
                                                        value={laneStart}
                                                        onChange={(e) => setLaneStart(e.target.value)}
                                                        style={{
                                                            flex: 1,
                                                            padding: "0.5rem",
                                                            border: `1px solid ${errors.laneStart ? '#dc3545' : color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: "0.8rem"
                                                        }}
                                                    >
                                                        <option value="">Start</option>
                                                        {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(letter => (
                                                            <option key={letter} value={letter}>{letter}</option>
                                                        ))}
                                                    </select>
                                                    <span style={{ fontSize: "0.9rem", color: color.fg }}>-</span>
                                                    <select
                                                        value={laneEnd}
                                                        onChange={(e) => setLaneEnd(e.target.value)}
                                                        style={{
                                                            flex: 1,
                                                            padding: "0.5rem",
                                                            border: `1px solid ${errors.laneEnd ? '#dc3545' : color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: "0.8rem"
                                                        }}
                                                    >
                                                        <option value="">End</option>
                                                        {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(letter => (
                                                            <option key={letter} value={letter}>{letter}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                // Single Lane Input
                                                <select
                                                    value={form.lane}
                                                    onChange={(e) => setForm(prev => ({ ...prev, lane: e.target.value }))}
                                                    style={{
                                                        width: "100%",
                                                        padding: "0.5rem",
                                                        border: `1px solid ${errors.lane ? '#dc3545' : color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg,
                                                    }}
                                                >
                                                    <option value="">Select</option>
                                                    {['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z'].map(letter => (
                                                        <option key={letter} value={letter}>{letter}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {(errors.lane || errors.laneStart || errors.laneEnd) && (
                                                <div style={{ color: '#dc3545', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                                                    {errors.lane || errors.laneStart || errors.laneEnd}
                                                </div>
                                            )}
                                        </div>

                                        {/* Location */}
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                                                <label style={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                                                    {createMultiple && !pallet ? "Location Range *" : "Location *"}
                                                </label>
                                                {!pallet && (
                                                    <label style={{ 
                                                        display: "flex", 
                                                        alignItems: "center", 
                                                        cursor: "pointer",
                                                        fontSize: "0.75rem",
                                                        color: color.fg,
                                                        opacity: 0.8
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={createMultiple}
                                                            onChange={(e) => {
                                                                setCreateMultiple(e.target.checked);
                                                                // Reset all range fields when toggling
                                                                if (e.target.checked) {
                                                                    setForm(prev => ({ ...prev, location: "", lane: "", position: "" }));
                                                                    setLocationStart("");
                                                                    setLocationEnd("");
                                                                    setLaneStart("");
                                                                    setLaneEnd("");
                                                                    setPositionStart("");
                                                                    setPositionEnd("");
                                                                } else {
                                                                    setLocationStart("");
                                                                    setLocationEnd("");
                                                                    setLaneStart("");
                                                                    setLaneEnd("");
                                                                    setPositionStart("");
                                                                    setPositionEnd("");
                                                                }
                                                                setErrors({});
                                                            }}
                                                            style={{ marginRight: "0.25rem", transform: "scale(0.8)" }}
                                                        />
                                                        Multiple
                                                    </label>
                                                )}
                                            </div>
                                            {createMultiple && !pallet ? (
                                                // Location Range Inputs
                                                <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                                    <input
                                                        type="text"
                                                        value={locationStart}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                                            setLocationStart(value);
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            padding: "0.5rem",
                                                            border: `1px solid ${errors.locationStart ? '#dc3545' : color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: "0.8rem"
                                                        }}
                                                        placeholder="1"
                                                        maxLength="3"
                                                    />
                                                    <span style={{ fontSize: "0.9rem", color: color.fg }}>-</span>
                                                    <input
                                                        type="text"
                                                        value={locationEnd}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                                            setLocationEnd(value);
                                                        }}
                                                        style={{
                                                            flex: 1,
                                                            padding: "0.5rem",
                                                            border: `1px solid ${errors.locationEnd ? '#dc3545' : color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: "0.8rem"
                                                        }}
                                                        placeholder="15"
                                                        maxLength="3"
                                                    />
                                                </div>
                                            ) : (
                                                // Single Location Input
                                                <input
                                                    type="text"
                                                    value={form.location}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                                        setForm(prev => ({ ...prev, location: value }));
                                                    }}
                                                    style={{
                                                        width: "100%",
                                                        padding: "0.5rem",
                                                        border: `1px solid ${errors.location ? '#dc3545' : color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg,
                                                    }}
                                                    placeholder="001"
                                                    maxLength="3"
                                                />
                                            )}
                                            {(errors.location || errors.locationStart || errors.locationEnd) && (
                                                <div style={{ color: '#dc3545', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                                                    {errors.location || errors.locationStart || errors.locationEnd}
                                                </div>
                                            )}
                                        </div>

                                        {/* Position */}
                                        <div>
                                            <label style={{ fontWeight: "bold", fontSize: "0.9rem", marginBottom: "0.25rem", display: "block", textAlign: "left" }}>
                                                {createMultiple && !pallet ? "Position Range *" : "Position *"}
                                            </label>
                                            {createMultiple && !pallet ? (
                                                // Position Range Inputs
                                                <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                                                    <select
                                                        value={positionStart}
                                                        onChange={(e) => setPositionStart(e.target.value)}
                                                        style={{
                                                            flex: 1,
                                                            padding: "0.5rem",
                                                            border: `1px solid ${errors.positionStart ? '#dc3545' : color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: "0.8rem"
                                                        }}
                                                    >
                                                        <option value="">Start</option>
                                                        {PALLET_HEIGHTS.map(letter => (
                                                            <option key={letter} value={letter}>{letter}</option>
                                                        ))}
                                                    </select>
                                                    <span style={{ fontSize: "0.9rem", color: color.fg }}>-</span>
                                                    <select
                                                        value={positionEnd}
                                                        onChange={(e) => setPositionEnd(e.target.value)}
                                                        style={{
                                                            flex: 1,
                                                            padding: "0.5rem",
                                                            border: `1px solid ${errors.positionEnd ? '#dc3545' : color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.bg,
                                                            color: color.fg,
                                                            fontSize: "0.8rem"
                                                        }}
                                                    >
                                                        <option value="">End</option>
                                                        {PALLET_HEIGHTS.map(letter => (
                                                            <option key={letter} value={letter}>{letter}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : (
                                                // Single Position Input
                                                <select
                                                    value={form.position}
                                                    onChange={(e) => setForm(prev => ({ ...prev, position: e.target.value }))}
                                                    style={{
                                                        width: "100%",
                                                        padding: "0.5rem",
                                                        border: `1px solid ${errors.position ? '#dc3545' : color.border}`,
                                                        borderRadius: 4,
                                                        backgroundColor: color.bg,
                                                        color: color.fg,
                                                    }}
                                                >
                                                    <option value="">Select</option>
                                                    {PALLET_HEIGHTS.map(letter => (
                                                        <option key={letter} value={letter}>{letter}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {(errors.position || errors.positionStart || errors.positionEnd) && (
                                                <div style={{ color: '#dc3545', fontSize: '0.7rem', marginTop: '0.25rem' }}>
                                                    {errors.position || errors.positionStart || errors.positionEnd}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Generated Pallet Code Preview */}
                                    {(form.pallet_code || (createMultiple && form.warehouse_no && laneStart && laneEnd && locationStart && locationEnd && positionStart && positionEnd)) && (
                                        <div style={{ 
                                            padding: "0.5rem", 
                                            backgroundColor: color.headerBg, 
                                            border: `1px solid ${color.border}`,
                                            borderRadius: 4,
                                            marginTop: "0.5rem"
                                        }}>
                                            {createMultiple ? (
                                                <>
                                                    <strong>Generated Pallet Codes: </strong>
                                                    <div style={{ marginTop: "0.25rem" }}>
                                                        <span style={{ 
                                                            fontSize: "1.1rem", 
                                                            fontWeight: "bold", 
                                                            color: color.highlight 
                                                        }}>
                                                            W{form.warehouse_no}{laneStart}{locationStart.padStart(3, '0')}{positionStart} - W{form.warehouse_no}{laneEnd}{locationEnd.padStart(3, '0')}{positionEnd}
                                                        </span>
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: "0.8rem", 
                                                        color: color.fg,
                                                        marginTop: "0.25rem",
                                                        opacity: 0.8
                                                    }}>
                                                        Will create {(() => {
                                                            const laneCount = (laneEnd.charCodeAt(0) - laneStart.charCodeAt(0) + 1);
                                                            const locationCount = (parseInt(locationEnd) - parseInt(locationStart) + 1);
                                                            const positionCount = (positionEnd.charCodeAt(0) - positionStart.charCodeAt(0) + 1);
                                                            return laneCount * locationCount * positionCount;
                                                        })()} pallets (Lanes: {laneStart}-{laneEnd}, Locations: {locationStart}-{locationEnd}, Positions: {positionStart}-{positionEnd})
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <strong>Generated Pallet Code: </strong>
                                                    <span style={{ 
                                                        fontSize: "1.1rem", 
                                                        fontWeight: "bold", 
                                                        color: color.highlight 
                                                    }}>
                                                        {form.pallet_code}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {/* Pallet Code Error Display */}
                            {errors.pallet_code && (
                                <div style={{ 
                                    color: '#dc3545', 
                                    fontSize: '0.8rem', 
                                    marginTop: '0.5rem',
                                    padding: "0.5rem",
                                    backgroundColor: "#f8d7da",
                                    border: "1px solid #f5c6cb",
                                    borderRadius: 4
                                }}>
                                    <i className="fas fa-exclamation-triangle" style={{ marginRight: "0.5rem" }}></i>
                                    {errors.pallet_code}
                                </div>
                            )}
                        </div>

                        {/* Pallet Type, and Notes in the same row */}
                        <div style={{ 
                            gridColumn: "1 / -1",
                            display: "grid", 
                            gridTemplateColumns: "2fr 2fr", 
                            gap: "1rem",
                            marginBottom: "1rem"
                        }}>

                            <div>
                                <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                    Pallet Type
                                </label>
                                <select
                                    value={form.pallet_type}
                                    onChange={(e) => setForm(prev => ({ ...prev, pallet_type: e.target.value }))}
                                    disabled={readOnly}
                                    style={{
                                        width: "100%",
                                        padding: "0.5rem",
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 4,
                                        backgroundColor: color.bg,
                                        color: color.fg,
                                    }}
                                >
                                    <option value="mixed_sku">Mixed SKU</option>
                                    <option value="single_sku">Single SKU</option>
                                    <option value="standard">Standard</option>
                                </select>
                            </div>

                            <div>
                                <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: "bold" }}>
                                    Notes
                                </label>
                                <input
                                    type="text"
                                    value={form.notes}
                                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                                    disabled={readOnly}
                                    style={{
                                        width: "100%",
                                        padding: "0.5rem",
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 4,
                                        backgroundColor: color.bg,
                                        color: color.fg,
                                    }}
                                    placeholder="Optional notes..."
                                />
                            </div>

                        {/* Status Toggle - Only show when editing existing pallet */}
                        {pallet && (
                            <div style={{ 
                                gridColumn: "1 / -1",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                marginTop: "1rem",
                                marginBottom: "1rem"
                            }}>
                                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                                    Pallet Status
                                </label>
                                <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
                                    <label style={{ display: "flex", alignItems: "center", cursor: canEdit ? "pointer" : "not-allowed" }}>
                                        <input
                                            type="radio"
                                            name="status"
                                            value="active"
                                            checked={form.status === "active"}
                                            onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                                            disabled={!canEdit}
                                            style={{ marginRight: "0.5rem" }}
                                        />
                                        <span style={{ color: "#28a745", fontWeight: "bold" }}>Active</span>
                                    </label>
                                    <label style={{ display: "flex", alignItems: "center", cursor: (canEdit && palletItems.length === 0) ? "pointer" : "not-allowed" }}>
                                        <input
                                            type="radio"
                                            name="status"
                                            value="inactive"
                                            checked={form.status === "inactive"}
                                            onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                                            disabled={!canEdit || palletItems.length > 0}
                                            style={{ marginRight: "0.5rem" }}
                                        />
                                        <span style={{ 
                                            color: (palletItems.length > 0 || !canEdit) ? "#6c757d" : "#dc3545", 
                                            fontWeight: "bold" 
                                        }}>
                                            Inactive {palletItems.length > 0 ? "(Empty pallet required)" : ""}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}
                        </div>
                    </div>

                    <div style={{ 
                        display: "flex", 
                        gap: "0.5rem", 
                        justifyContent: "flex-end",
                        paddingTop: "1rem",
                        borderTop: `1px solid ${color.border}`
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                padding: "0.5rem 1rem",
                                border: `1px solid ${color.border}`,
                                backgroundColor: color.bg,
                                color: color.fg,
                                borderRadius: 4,
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        
                        {/* Delete button - only show for existing pallets
                        {pallet && pallet.id && canEdit && (
                            <button
                                type="button"
                                onClick={console.log("Delete Pallet functionality not implemented yet")} // Placeholder for delete functionality
                                disabled={loading}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: "#dc3545", // Red color for delete
                                    color: "#fff",
                                    borderRadius: 4,
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading ? "Deleting..." : "Delete Pallet"}
                            </button>
                        )} */}
                        
                        {/* Move Pallet Location button - only show for existing pallets and admin/warehouse users */}
                        {pallet && pallet.id && canEdit && (!isCustomer || userProfile?.group === 'ADMIN' || userProfile?.group === 'WAREHOUSE') && (
                            <button
                                type="button"
                                onClick={() => setShowMovePalletLocationModal(true)}
                                disabled={loading}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: "#ffc107", // Yellow/orange color
                                    color: "#000",
                                    borderRadius: 4,
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                Move All Products
                            </button>
                        )}
                        
                        {canEdit && (
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: color.highlight,
                                    color: "#000",
                                    borderRadius: 4,
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading ? "Creating..." : (pallet ? "Update Pallet" : createMultiple ? `Create ${(() => {
                                    const laneCount = (laneEnd && laneStart) ? (laneEnd.charCodeAt(0) - laneStart.charCodeAt(0) + 1) : 0;
                                    const locationCount = (locationEnd && locationStart) ? (parseInt(locationEnd || 0) - parseInt(locationStart || 0) + 1) : 0;
                                    const positionCount = (positionEnd && positionStart) ? (positionEnd.charCodeAt(0) - positionStart.charCodeAt(0) + 1) : 0;
                                    return laneCount * locationCount * positionCount;
                                })()} Pallets` : "Create Pallet")}
                            </button>
                        )}
                    </div>
                </form>

                {/* Current Pallet Items - Only for existing pallets */}
                {pallet && palletItems.length > 0 && (
                    <div style={{
                        borderTop: `1px solid ${color.border}`,
                        padding: "1rem",
                        backgroundColor: color.bg,
                    }}>
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ 
                                width: "100%", 
                                borderCollapse: "collapse",
                                fontSize: "1rem",
                            }}>
                                <thead>
                                    <tr style={{ 
                                        backgroundColor: isDark ? "#fff" : "#333",
                                        color: isDark ? "#000" : "#fff",
                                    }}>
                                        <th style={{ 
                                            padding: "0.5rem 1.5rem", 
                                            textAlign: "left",
                                            fontWeight: "600"
                                        }}>Product Code</th>
                                        <th style={{ 
                                            padding: "0.5rem 1.5rem", 
                                            textAlign: "left",
                                            fontWeight: "600"
                                        }}>Product Name</th>
                                        <th style={{ 
                                            padding: "0.5rem 1.5rem", 
                                            textAlign: "left",
                                            fontWeight: "600"
                                        }}>Batch No</th>
                                        <th style={{ 
                                            padding: "0.5rem 1.5rem", 
                                            textAlign: "center",
                                            fontWeight: "600"
                                        }}>Quantity</th>
                                        <th style={{ 
                                            padding: "0.5rem 1.5rem", 
                                            textAlign: "left",
                                            fontWeight: "600"
                                        }}>Notes</th>
                                        {canEdit && (
                                            <th style={{ 
                                                padding: "0.5rem 1.5rem", 
                                                textAlign: "center",
                                                fontWeight: "600"
                                            }}>Actions</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {palletItems.map((item, index) => {
                                        const isEditing = editingItemId === item.id;
                                        const isRemoving = removingItemId === item.id;
                                        const isEven = index % 2 === 0;
                                        const currentQuantity = editedQuantities[item.id] !== undefined ? editedQuantities[item.id] : item.quantity;
                                        const currentNotes = editedNotes[item.id] !== undefined ? editedNotes[item.id] : (item.notes || '');
                                        const hasQuantityChanges = editedQuantities[item.id] !== undefined && editedQuantities[item.id] !== item.quantity;
                                        const hasNotesChanges = editedNotes[item.id] !== undefined && editedNotes[item.id] !== (item.notes || '');
                                        const hasChanges = hasQuantityChanges || hasNotesChanges;
                                        
                                        return (
                                            <tr key={item.id} style={{
                                                backgroundColor: color.panel,
                                                borderBottom: `1px solid ${color.border}`
                                            }}>
                                                <td style={{ 
                                                    padding: "0.5rem 1.5rem",
                                                    fontFamily: "monospace",
                                                    fontWeight: "normal",
                                                    fontSize: "1rem",
                                                    textAlign: "left"
                                                }}>
                                                    {item.product?.product_code || 'N/A'}
                                                </td>
                                                <td style={{ 
                                                    padding: "0.5rem 1.5rem",
                                                    fontSize: "1rem",
                                                    textAlign: "left"
                                                }}>
                                                    {item.product?.product_name || 'N/A'}
                                                </td>
                                                <td style={{ 
                                                    padding: "0.5rem 1.5rem",
                                                    fontFamily: "monospace",
                                                    fontSize: "1rem",
                                                    textAlign: "left"
                                                }}>
                                                    {item.product?.batch_no || '-'}
                                                </td>
                                                <td style={{ 
                                                    padding: "0.5rem 1.5rem", 
                                                    textAlign: "center"
                                                }}>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={currentQuantity}
                                                            onChange={(e) => {
                                                                const newQuantity = parseInt(e.target.value) || 0;
                                                                setEditedQuantities(prev => ({
                                                                    ...prev,
                                                                    [item.id]: newQuantity
                                                                }));
                                                            }}
                                                            style={{
                                                                width: "100px",
                                                                padding: "0.5rem",
                                                                border: "none",
                                                                borderRadius: 4,
                                                                backgroundColor: color.panel,
                                                                color: color.fg,
                                                                textAlign: "center",
                                                                fontSize: "1rem",
                                                                fontWeight: "600"
                                                            }}
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    const newQuantity = parseInt(e.target.value);
                                                                    if (newQuantity > 0) {
                                                                        updateItemQuantity(item.id, newQuantity, currentNotes);
                                                                    }
                                                                } else if (e.key === 'Escape') {
                                                                    setEditingItemId(null);
                                                                    setEditedQuantities(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated[item.id];
                                                                        return updated;
                                                                    });
                                                                    setEditedNotes(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated[item.id];
                                                                        return updated;
                                                                    });
                                                                }
                                                            }}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <span 
                                                            onClick={() => {
                                                                if (readOnly) return;
                                                                setEditingItemId(item.id);
                                                                setEditedQuantities(prev => ({
                                                                    ...prev,
                                                                    [item.id]: item.quantity
                                                                }));
                                                                setEditedNotes(prev => ({
                                                                    ...prev,
                                                                    [item.id]: item.notes || ''
                                                                }));
                                                            }}
                                                            style={{ 
                                                                cursor: readOnly ? "default" : "pointer", 
                                                                padding: "0.5rem 1rem",
                                                                backgroundColor: "transparent",
                                                                color: color.fg,
                                                                display: "inline-block",
                                                                minWidth: "50px",
                                                                fontWeight: "600",
                                                                fontSize: "1rem",
                                                                textDecoration: "underline"
                                                            }}
                                                            title={readOnly ? "Quantity" : "Click to edit quantity and notes"}
                                                        >
                                                            {currentQuantity}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ 
                                                    padding: "0.5rem 1.5rem", 
                                                    textAlign: "left"
                                                }}>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={currentNotes}
                                                            onChange={(e) => {
                                                                setEditedNotes(prev => ({
                                                                    ...prev,
                                                                    [item.id]: e.target.value
                                                                }));
                                                            }}
                                                            style={{
                                                                width: "200px",
                                                                padding: "0.5rem",
                                                                border: "none",
                                                                borderRadius: 4,
                                                                backgroundColor: color.panel,
                                                                color: color.fg,
                                                                fontSize: "0.9rem"
                                                            }}
                                                            placeholder="Add notes..."
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    if (currentQuantity > 0) {
                                                                        updateItemQuantity(item.id, currentQuantity, currentNotes);
                                                                    }
                                                                } else if (e.key === 'Escape') {
                                                                    setEditingItemId(null);
                                                                    setEditedQuantities(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated[item.id];
                                                                        return updated;
                                                                    });
                                                                    setEditedNotes(prev => {
                                                                        const updated = { ...prev };
                                                                        delete updated[item.id];
                                                                        return updated;
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <span 
                                                            onClick={() => {
                                                                if (readOnly) return;
                                                                setEditingItemId(item.id);
                                                                setEditedQuantities(prev => ({
                                                                    ...prev,
                                                                    [item.id]: item.quantity
                                                                }));
                                                                setEditedNotes(prev => ({
                                                                    ...prev,
                                                                    [item.id]: item.notes || ''
                                                                }));
                                                            }}
                                                            style={{ 
                                                                cursor: readOnly ? "default" : "pointer", 
                                                                padding: "0.5rem",
                                                                backgroundColor: "transparent",
                                                                color: color.fg,
                                                                display: "inline-block",
                                                                minWidth: "100px",
                                                                fontSize: "0.9rem",
                                                                fontStyle: (item.notes ? "normal" : "italic"),
                                                                opacity: (item.notes ? 1 : 0.6)
                                                            }}
                                                            title={readOnly ? "Notes" : "Click to edit notes"}
                                                        >
                                                            {item.notes || 'Add notes...'}
                                                        </span>
                                                    )}
                                                </td>
                                                {canEdit && (
                                                    <td style={{ 
                                                        padding: "0.5rem 1.5rem", 
                                                        textAlign: "center"
                                                    }}>
                                                        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
                                                            {isEditing ? (
                                                                <button
                                                                    onClick={() => {
                                                                        if (hasChanges && currentQuantity > 0) {
                                                                            updateItemQuantity(item.id, currentQuantity, currentNotes);
                                                                        } else {
                                                                            setEditingItemId(null);
                                                                            setEditedQuantities(prev => {
                                                                                const updated = { ...prev };
                                                                                delete updated[item.id];
                                                                                return updated;
                                                                            });
                                                                            setEditedNotes(prev => {
                                                                                const updated = { ...prev };
                                                                                delete updated[item.id];
                                                                                return updated;
                                                                            });
                                                                        }
                                                                    }}
                                                                    disabled={isRemoving || (hasChanges && currentQuantity <= 0)}
                                                                    style={{
                                                                        background: hasChanges ? "#28a745" : "#6c757d",
                                                                        color: "#fff",
                                                                        border: "none",
                                                                        borderRadius: 4,
                                                                        padding: "0.5rem 1rem",
                                                                        cursor: (isRemoving || (hasChanges && currentQuantity <= 0)) ? "not-allowed" : "pointer",
                                                                        fontSize: "0.9rem",
                                                                        fontWeight: "600",
                                                                        opacity: (isRemoving || (hasChanges && currentQuantity <= 0)) ? 0.5 : 1,
                                                                        minWidth: "90px",
                                                                        textAlign: "center"
                                                                    }}
                                                                    title={hasChanges ? "Save changes" : "Cancel editing"}
                                                                >
                                                                    {hasChanges ? "Save" : "Cancel"}
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingItemId(item.id);
                                                                        setEditedQuantities(prev => ({
                                                                            ...prev,
                                                                            [item.id]: item.quantity
                                                                        }));
                                                                        setEditedNotes(prev => ({
                                                                            ...prev,
                                                                            [item.id]: item.notes || ''
                                                                        }));
                                                                    }}
                                                                    disabled={isRemoving}
                                                                    style={{
                                                                        background: "#007bff",
                                                                        color: "#fff",
                                                                        border: "none",
                                                                        borderRadius: 4,
                                                                        padding: "0.5rem 1rem",
                                                                        cursor: isRemoving ? "not-allowed" : "pointer",
                                                                        fontSize: "0.9rem",
                                                                        fontWeight: "600",
                                                                        opacity: isRemoving ? 0.5 : 1,
                                                                        minWidth: "90px",
                                                                        textAlign: "center"
                                                                    }}
                                                                    title="Edit quantity and notes"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => {
                                                                    setMovingItemId(item.id);
                                                                    setShowMoveModal(true);
                                                                }}
                                                                disabled={isRemoving || isEditing || movingItemId === item.id || availablePalletsForMove.length === 0}
                                                                style={{
                                                                    background: availablePalletsForMove.length === 0 ? "#6c757d" : "#ffc107",
                                                                    color: availablePalletsForMove.length === 0 ? "#fff" : "#000",
                                                                    border: "none",
                                                                    borderRadius: 4,
                                                                    padding: "0.5rem 1rem",
                                                                    cursor: (isRemoving || isEditing || movingItemId === item.id || availablePalletsForMove.length === 0) ? "not-allowed" : "pointer",
                                                                    fontSize: "0.9rem",
                                                                    fontWeight: "600",
                                                                    opacity: (isRemoving || isEditing || movingItemId === item.id || availablePalletsForMove.length === 0) ? 0.5 : 1,
                                                                    minWidth: "90px",
                                                                    textAlign: "center"
                                                                }}
                                                                title={availablePalletsForMove.length === 0 ? "No other pallets available for this customer" : "Move item to another pallet"}
                                                            >
                                                                {movingItemId === item.id ? "..." : "Move"}
                                                            </button>
                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                disabled={isRemoving || isEditing}
                                                                style={{
                                                                    background: "#dc3545",
                                                                    color: "#fff",
                                                                    border: "none",
                                                                    borderRadius: 4,
                                                                    padding: "0.5rem 1rem",
                                                                    cursor: (isRemoving || isEditing) ? "not-allowed" : "pointer",
                                                                    fontSize: "0.9rem",
                                                                    fontWeight: "600",
                                                                    opacity: (isRemoving || isEditing) ? 0.5 : 1,
                                                                    minWidth: "90px",
                                                                    textAlign: "center"
                                                                }}
                                                                title="Remove item from pallet"
                                                            >
                                                                {isRemoving ? "..." : "Delete"}
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Add Product Section - Only for existing pallets */}
                {pallet && canEdit && (
                    <div style={{
                        borderTop: `1px solid ${color.border}`,
                        padding: "1rem",
                        minHeight: "450px",
                        backgroundColor: color.bg,
                    }}>
                        <h3 style={{ margin: "0 0 1rem 0" }}>Add Product to Pallet</h3>
                        
                        {/* Show message for inactive pallets */}
                        {form.status === 'inactive' && (
                            <div style={{
                                padding: "1rem",
                                backgroundColor: "#f8d7da",
                                border: "1px solid #f5c6cb",
                                borderRadius: "4px",
                                color: "#721c24",
                                textAlign: "center",
                                marginBottom: "1rem"
                            }}>
                                <strong>This pallet is inactive.</strong><br />
                                Products cannot be added to inactive pallets. Set the pallet to "Active" to add products.
                            </div>
                        )}
                        
                        {/* Only show add product form if pallet is active */}
                        {form.status === 'active' && (
                            <div style={{ 
                                display: "grid", 
                                gridTemplateColumns: "2fr 1fr 2fr 1fr", 
                                gap: "0.5rem",
                                alignItems: "end"
                            }}>
                            <div style={{ position: 'relative' }} ref={productDropdownRef}>
                                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                                    Product
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        value={productSearchTerm}
                                        onChange={handleProductSearchChange}
                                        onFocus={() => setShowProductDropdown(true)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                setShowProductDropdown(false);
                                            }
                                        }}
                                        placeholder="Search by product name, code, or batch..."
                                        style={{
                                            width: "100%",
                                            padding: "0.5rem",
                                            paddingRight: selectedProduct ? "2rem" : "0.5rem",
                                            border: `1px solid ${color.border}`,
                                            borderRadius: 4,
                                            backgroundColor: color.panel,
                                            color: color.fg,
                                        }}
                                    />
                                    
                                    {/* Clear button */}
                                    {selectedProduct && (
                                        <button
                                            type="button"
                                            onClick={clearProductSelection}
                                            style={{
                                                position: 'absolute',
                                                right: '0.5rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: color.fg,
                                                fontSize: '1rem',
                                                padding: '0',
                                                lineHeight: 1,
                                            }}
                                            title="Clear selection"
                                        >
                                            ×
                                        </button>
                                    )}
                                    
                                    {/* Dropdown list */}
                                    {showProductDropdown && customerProducts.length > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: color.panel,
                                            border: `2px solid ${color.border}`,
                                            borderTop: 'none',
                                            borderRadius: '0 0 6px 6px',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            zIndex: 9999,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        }}>
                                            {customerProducts.slice(0, 8).map((product, index) => (
                                                <div
                                                    key={product.id}
                                                    onClick={() => handleProductSelect(product)}
                                                    style={{
                                                        padding: '0.75rem',
                                                        cursor: 'pointer',
                                                        borderBottom: index < Math.min(customerProducts.length, 8) - 1 ? `1px solid ${color.border}` : 'none',
                                                        backgroundColor: color.panel,
                                                        color: color.fg,
                                                        transition: 'background-color 0.15s ease',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = color.highlight || '#e3f2fd';
                                                        e.currentTarget.style.color = '#000';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = color.panel;
                                                        e.currentTarget.style.color = color.fg;
                                                    }}
                                                >
                                                    {/* Product Name (Primary) */}
                                                    <div style={{ 
                                                        fontWeight: '600', 
                                                        fontSize: '0.95rem',
                                                        marginBottom: '0.2rem',
                                                        lineHeight: '1.2',
                                                    }}>
                                                        {product.product_name}
                                                    </div>
                                                    {/* Product Code and Batch (Secondary) */}
                                                    <div style={{ 
                                                        fontSize: '0.85rem', 
                                                        opacity: 0.8,
                                                        fontFamily: 'monospace',
                                                        color: 'inherit',
                                                    }}>
                                                        Code: {product.product_code}
                                                        {product.batch_no && ` • Batch: ${product.batch_no}`}
                                                    </div>
                                                </div>
                                            ))}
                                            
                                            {customerProducts.length > 8 && (
                                                <div style={{
                                                    padding: '0.5rem 0.75rem',
                                                    fontSize: '0.8rem',
                                                    fontStyle: 'italic',
                                                    color: color.fg,
                                                    opacity: 0.6,
                                                    textAlign: 'center',
                                                    backgroundColor: color.headerBg || color.panel,
                                                    borderTop: `1px solid ${color.border}`,
                                                }}>
                                                    {customerProducts.length - 8} more products available. Keep typing to narrow results.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* No results message */}
                                    {showProductDropdown && productSearchTerm && customerProducts.length === 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: color.panel,
                                            border: `2px solid ${color.border}`,
                                            borderTop: 'none',
                                            borderRadius: '0 0 6px 6px',
                                            padding: '1rem 0.75rem',
                                            zIndex: 9999,
                                            textAlign: 'center',
                                            color: color.fg,
                                            fontSize: '0.9rem',
                                            fontStyle: 'italic',
                                            opacity: 0.7,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        }}>
                                            No products found matching "<strong>{productSearchTerm}</strong>"
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                                    Quantity
                                </label>
                                <input
                                    type="number"
                                    value={productQuantity}
                                    onChange={(e) => setProductQuantity(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "0.5rem",
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 4,
                                        backgroundColor: color.panel,
                                        color: color.fg,
                                    }}
                                    min="1"
                                    placeholder="Qty"
                                />
                            </div>

                            <div>
                                <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                                    Notes
                                </label>
                                <input
                                    type="text"
                                    value={productNotes}
                                    onChange={(e) => setProductNotes(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "0.5rem",
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 4,
                                        backgroundColor: color.panel,
                                        color: color.fg,
                                    }}
                                    placeholder="Optional..."
                                />
                            </div>

                            <button
                                onClick={addProductToPallet}
                                disabled={addingProduct || !selectedProductValid || !productQuantity}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: addingProduct ? color.warning : color.success,
                                    color: "#fff",
                                    borderRadius: 4,
                                    cursor: (addingProduct || !selectedProductValid || !productQuantity) ? "not-allowed" : "pointer",
                                    opacity: (addingProduct || !selectedProductValid || !productQuantity) ? 0.7 : 1,
                                }}
                            >
                                {addingProduct ? "Adding..." : "Add"}
                            </button>
                        </div>
                        )}
                        
                        {customerProducts.length === 0 && form.customer_id && (
                            <div style={{ 
                                marginTop: "1rem", 
                                padding: "0.5rem", 
                                backgroundColor: color.warning + "20",
                                borderRadius: 4,
                                fontSize: "0.9rem"
                            }}>
                                No products available for this customer. Create products first.
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* Move Item Modal */}
            {showMoveModal && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1002,
                }}>
                    <div style={{
                        backgroundColor: color.panel,
                        borderRadius: 8,
                        textAlign: "center",
                        padding: "1.5rem",
                        minWidth: "500px",
                        maxWidth: "90vw",
                        border: `1px solid ${color.border}`,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}>
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "1rem",
                            position: "relative"
                        }}>
                            <h3 style={{ 
                                margin: 0,
                                color: color.fg,
                                textAlign: "center",
                                flex: 1
                            }}>
                                Move Product
                            </h3>
                            <button
                                onClick={() => {
                                    setShowMoveModal(false);
                                    setDestinationPalletId("");
                                    setMovingItemId(null);
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "1.5rem",
                                    cursor: "pointer",
                                    color: color.fg,
                                    position: "absolute",
                                    right: 0
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ marginBottom: "1rem" }}>
                            <label style={{ 
                                display: "block", 
                                marginBottom: "0.5rem", 
                                fontWeight: "bold",
                                color: color.fg 
                            }}>
                                Select Destination Pallet:
                            </label>
                            <div style={{ position: 'relative' }} ref={destinationDropdownRef}>
                                <input
                                    type="text"
                                    value={destinationPalletSearch}
                                    onChange={handleDestinationPalletSearchChange}
                                    onFocus={() => setShowDestinationDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                            setShowDestinationDropdown(false);
                                        }
                                    }}
                                    placeholder="Search by pallet code or warehouse..."
                                    style={{
                                        width: "100%",
                                        padding: "0.5rem",
                                        paddingRight: selectedDestinationPallet ? "2rem" : "0.5rem",
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 4,
                                        backgroundColor: color.bg,
                                        color: color.fg,
                                    }}
                                />
                                
                                {/* Clear button */}
                                {selectedDestinationPallet && (
                                    <button
                                        onClick={clearDestinationPalletSelection}
                                        style={{
                                            position: 'absolute',
                                            right: '0.5rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            color: color.fg,
                                            cursor: 'pointer',
                                            padding: '0',
                                            fontSize: '1rem'
                                        }}
                                        title="Clear selection"
                                    >
                                        ✕
                                    </button>
                                )}
                                
                                {/* Dropdown */}
                                {showDestinationDropdown && filteredDestinationPallets.length > 0 && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        backgroundColor: color.panel,
                                        border: `1px solid ${color.border}`,
                                        borderTop: 'none',
                                        borderRadius: '0 0 4px 4px',
                                        zIndex: 1000,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                    }}>
                                        {filteredDestinationPallets.map(pallet => {
                                            const hasItems = pallet.items && pallet.items.length > 0;
                                            const isOccupied = pallet.occupied || hasItems;
                                            const totalQuantity = pallet.total_quantity || 0;
                                            const palletId = pallet.pallet_id; // Adjust based on your data structure
                                            console.log("Rendering pallet:", pallet);
                                            
                                            return (
                                                <div
                                                    key={palletId}
                                                    onClick={() => selectDestinationPallet(pallet)}
                                                    style={{
                                                        padding: '0.75rem',
                                                        cursor: 'pointer',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        backgroundColor: selectedDestinationPallet?.pallet_id === palletId ? color.highlight : 'transparent',
                                                        color: color.fg,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.75rem'
                                                    }}
                                                >
                                                    {/* Occupancy Status Badge */}
                                                    <div style={{
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        backgroundColor: isOccupied ? '#dc3545' : '#28a745',
                                                        color: 'white',
                                                        minWidth: '70px',
                                                        textAlign: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        {isOccupied ? 'OCCUPIED' : 'AVAILABLE'}
                                                    </div>
                                                    
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ 
                                                            fontWeight: 'bold',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.5rem'
                                                        }}>
                                                            {pallet.pallet_code}
                                                            {pallet.pallet_type && (
                                                                <span style={{
                                                                    fontSize: '0.7rem',
                                                                    backgroundColor: '#6c757d',
                                                                    color: 'white',
                                                                    padding: '0.15rem 0.4rem',
                                                                    borderRadius: '3px'
                                                                }}>
                                                                    {pallet.pallet_type.toUpperCase()}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '0.85rem', opacity: 0.8, display: 'flex', gap: '1rem' }}>
                                                            <span>Lane: {pallet.lane_id}</span>
                                                            {isOccupied && (
                                                                <span style={{ color: '#ffc107', fontWeight: 'bold' }}>
                                                                    {hasItems ? `${pallet.items.length} items` : `Qty: ${totalQuantity}`}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                {/* No results message */}
                                {showDestinationDropdown && filteredDestinationPallets.length === 0 && destinationPalletSearch.trim() && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: color.panel,
                                        border: `1px solid ${color.border}`,
                                        borderTop: 'none',
                                        borderRadius: '0 0 4px 4px',
                                        padding: '0.75rem',
                                        textAlign: 'center',
                                        color: color.fg,
                                        fontStyle: 'italic'
                                    }}>
                                        No pallets found matching "{destinationPalletSearch}"
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{
                            display: "flex",
                            gap: "0.75rem",
                            justifyContent: "flex-end",
                        }}>
                            <button
                                onClick={() => {
                                    setShowMoveModal(false);
                                    setDestinationPalletId("");
                                    setMovingItemId(null);
                                    clearDestinationPalletSelection();
                                }}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: `1px solid ${color.border}`,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                    borderRadius: 4,
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    if (destinationPalletId && movingItemId) {
                                        moveItem(movingItemId, destinationPalletId);
                                    }
                                }}
                                disabled={!destinationPalletId || !movingItemId}
                                style={{
                                    padding: "0.5rem 1rem",
                                    border: "none",
                                    backgroundColor: (!destinationPalletId || !movingItemId) ? color.muted : color.success,
                                    color: "#fff",
                                    borderRadius: 4,
                                    cursor: (!destinationPalletId || !movingItemId) ? "not-allowed" : "pointer",
                                    opacity: (!destinationPalletId || !movingItemId) ? 0.6 : 1,
                                }}
                            >
                                {isLoading ? "Moving..." : "Move Product"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* QR Code Modal */}
            {showQRModal && (form.qr_link || pallet?.qr_link) && (
                <QRViewModal
                    qrUrl={form.qr_link || pallet.qr_link}
                    title={`Pallet ${pallet?.pallet_code || form.pallet_code} QR Code`}
                    productId={pallet?.pallet_code || form.pallet_code}
                    productDescription={`Lane: ${pallet?.lane_id || 'Unknown'}`}
                    palletData={pallet}
                    onClose={() => setShowQRModal(false)}
                    onRegenerateQR={handleRegenerateQR}
                />
            )}

            {/* Move Pallet Location Modal */}
            {showMovePalletLocationModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                    backdropFilter: 'blur(3px)'
                }}>
                    <div style={{
                        backgroundColor: color.bg,
                        border: `1px solid ${color.border}`,
                        borderRadius: 8,
                        padding: '1.5rem',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '1.5rem'
                        }}>
                            <h3 style={{ 
                                margin: 0,
                                color: color.fg,
                                textAlign: "center",
                                flex: 1
                            }}>
                                Move Products to Different Location
                            </h3>
                            <button
                                onClick={() => {
                                    setShowMovePalletLocationModal(false);
                                    clearDestinationPalletSelectionForMove();
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    fontSize: "1.5rem",
                                    cursor: "pointer",
                                    color: color.fg
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ marginBottom: "1.5rem" }}>
                            <p style={{ 
                                color: color.secondary, 
                                marginBottom: "1rem",
                                fontSize: "0.9rem"
                            }}>
                                Moving all products from pallet <strong>{pallet?.pallet_code}</strong> at location <strong>{pallet?.lane_id}</strong> to an empty pallet at the specified location.
                            </p>
                            <p style={{ 
                                color: color.secondary, 
                                marginBottom: "1rem",
                                fontSize: "0.85rem",
                                fontStyle: "italic"
                            }}>
                                Note: This will move all products to an existing empty pallet at the destination lane.
                            </p>
                            
                            <label style={{ 
                                display: "block", 
                                marginBottom: "0.5rem",
                                fontSize: "0.9rem",
                                color: color.fg,
                                fontWeight: "500"
                            }}>
                                Select Destination Empty Pallet:
                            </label>
                            
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={destinationPalletSearchForMove}
                                    onChange={handleDestinationPalletSearchForMoveChange}
                                    onFocus={() => setShowDestinationPalletsDropdown(true)}
                                    placeholder="Search empty pallets by code or location..."
                                    style={{
                                        width: "100%",
                                        padding: "0.75rem",
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 4,
                                        backgroundColor: color.panel,
                                        color: color.fg,
                                        fontSize: "1rem"
                                    }}
                                    autoFocus
                                />
                                
                                {/* Dropdown */}
                                {showDestinationPalletsDropdown && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: color.panel,
                                        border: `1px solid ${color.border}`,
                                        borderTop: 'none',
                                        borderRadius: '0 0 4px 4px',
                                        maxHeight: '400px',
                                        overflowY: 'auto',
                                        zIndex: 1000,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                    }}>
                                        {filteredEmptyPallets.length > 0 ? (
                                            filteredEmptyPallets.map((pallet) => (
                                                <div
                                                    key={pallet.pallet_id}
                                                    className={!pallet.occupied ? 'pallet-option' : ''}
                                                    onClick={() =>
                                                        !pallet.occupied && selectDestinationPalletForMove(pallet)
                                                    }
                                                    style={{
                                                        padding: '0.75rem',
                                                        cursor: 'pointer',
                                                        borderBottom: `1px solid ${color.border}`,
                                                        color: color.fg,
                                                        backgroundColor:
                                                            pallet.occupied
                                                                ? color.Error
                                                                : selectedDestinationPalletForMove === pallet.pallet_id
                                                                    ? color.highlight
                                                                    : 'transparent'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            fontWeight: 'bold',
                                                            width: '100%',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {pallet.pallet_code}

                                                        <div
                                                            style={{
                                                                width: '40%',
                                                                margin: '4px auto 0',
                                                                border: '1px solid #ee0800',
                                                                backgroundColor: '#ce7500',
                                                                borderRadius: '5px',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {pallet.pallet_type
                                                                ? pallet.pallet_type.toUpperCase()
                                                                : 'Unknown Type'}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: color.secondary }}>
                                                        Location: {pallet.lane_id} • Empty
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{
                                                padding: '0.75rem',
                                                textAlign: 'center',
                                                color: color.secondary,
                                                fontStyle: 'italic'
                                            }}>
                                                {availableEmptyPallets.length === 0 
                                                    ? "No empty pallets available for this customer" 
                                                    : `No empty pallets found matching "${destinationPalletSearchForMove}"`
                                                }
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={{
                            display: "flex",
                            gap: "0.75rem",
                            justifyContent: "flex-end",
                        }}>
                            <button
                                onClick={() => {
                                    setShowMovePalletLocationModal(false);
                                    clearDestinationPalletSelectionForMove();
                                }}
                                disabled={loading}
                                style={{
                                    padding: "0.75rem 1.5rem",
                                    border: `1px solid ${color.border}`,
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                    borderRadius: 4,
                                    cursor: loading ? "not-allowed" : "pointer",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={movePalletToNewLocation}
                                disabled={loading || !destinationPalletIdForMove}
                                style={{
                                    padding: "0.75rem 1.5rem",
                                    border: "none",
                                    backgroundColor: !destinationPalletIdForMove ? "#6c757d" : "#ffc107",
                                    color: !destinationPalletIdForMove ? "#fff" : "#000",
                                    borderRadius: 4,
                                    cursor: (loading || !destinationPalletIdForMove) ? "not-allowed" : "pointer",
                                    fontWeight: "600"
                                }}
                            >
                                {loading ? "Moving..." : "Move Products"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

PalletEditor.propTypes = {
    pallet: PropTypes.object,
    allCustomers: PropTypes.arrayOf(PropTypes.object).isRequired,
    allPallets: PropTypes.arrayOf(PropTypes.object),
    color: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onRefreshData: PropTypes.func,
    onUpdatePallet: PropTypes.func,
    isCustomer: PropTypes.bool,
    userProfile: PropTypes.object,
    readOnly: PropTypes.bool,
};
